import React, { useState } from 'react'
import { X, Bug, Send } from 'lucide-react'
import { sendBugReport } from '../../services/bugReportService'

interface ReportBugModalProps {
  isOpen: boolean
  onClose: () => void
}

export function ReportBugModal({ isOpen, onClose }: ReportBugModalProps) {
  const [email, setEmail] = useState('')
  const [bugType, setBugType] = useState('functionality')
  const [description, setDescription] = useState('')
  const [steps, setSteps] = useState('')
  const [expectedBehavior, setExpectedBehavior] = useState('')
  const [browser, setBrowser] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Send bug report via EmailJS
      const result = await sendBugReport({
        email,
        bugType,
        description,
        steps,
        expectedBehavior,
        browser,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      })

      if (result.success) {
        setSubmitSuccess(true)
        
        // Reset form and close after 2 seconds
        setTimeout(() => {
          setEmail('')
          setBugType('functionality')
          setDescription('')
          setSteps('')
          setExpectedBehavior('')
          setBrowser('')
          setSubmitSuccess(false)
          onClose()
        }, 2000)
      } else {
        // If EmailJS fails, fallback to mailto
        console.error('EmailJS failed, falling back to mailto')
        const subject = encodeURIComponent(`Bug Report: ${bugType} - ${description.substring(0, 50)}...`)
        const body = encodeURIComponent(`
Bug Report
==========

Type: ${bugType}
Date: ${new Date().toLocaleString()}
URL: ${window.location.href}
Browser: ${browser || 'Not specified'}

Description:
${description}

Steps to Reproduce:
${steps}

Expected Behavior:
${expectedBehavior}

User Contact: ${email}

Technical Details:
User Agent: ${navigator.userAgent}
        `.trim())

        const mailtoLink = `mailto:vincentmasci@icloud.com?subject=${subject}&body=${body}`
        window.open(mailtoLink, '_blank')
        
        setSubmitSuccess(true)
        setTimeout(() => {
          setEmail('')
          setBugType('functionality')
          setDescription('')
          setSteps('')
          setExpectedBehavior('')
          setBrowser('')
          setSubmitSuccess(false)
          onClose()
        }, 2000)
      }
    } catch (error) {
      console.error('Bug report error:', error)
      alert('Failed to send bug report. Please try again or email us directly.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center" style={{ fontSize: 'min(1rem, 1.5vw)' }}>
      <div className="bg-gray-800/85 backdrop-blur-md rounded-xl shadow-2xl flex flex-col border border-gray-700/50" style={{ width: '70vw', maxWidth: '800px', height: '80vh', maxHeight: '600px' }}>
        {/* Header */}
        <div className="border-b border-gray-700 flex items-center justify-between bg-gray-800/90 backdrop-blur-sm" style={{ padding: '1em' }}>
          <div className="flex items-center" style={{ gap: '0.75em' }}>
            <div className="bg-red-600/20 rounded-lg flex items-center justify-center" style={{ width: '2.5em', height: '2.5em' }}>
              <Bug className="text-red-400" style={{ width: '1.25em', height: '1.25em' }} />
            </div>
            <div>
              <h2 className="font-bold text-white" style={{ fontSize: '1.25em' }}>Report a Bug</h2>
              <p className="text-gray-400" style={{ fontSize: '0.75em', marginTop: '0.125em' }}>Help us improve CYA Routes</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-2"
          >
            <X style={{ width: '1.25em', height: '1.25em' }} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto" style={{ padding: '1.5em' }}>
            {submitSuccess ? (
              <div className="text-center" style={{ padding: '3em 0' }}>
                <div className="bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto" style={{ width: '4em', height: '4em', marginBottom: '1em' }}>
                  <Send className="text-emerald-400" style={{ width: '2em', height: '2em' }} />
                </div>
                <h3 className="font-semibold text-white" style={{ fontSize: '1.25em', marginBottom: '0.5em' }}>Bug Report Sent!</h3>
                <p className="text-gray-400" style={{ fontSize: '0.875em' }}>Thank you for helping us improve CYA Routes.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Email */}
                <div>
                  <label htmlFor="email" className="block font-medium text-gray-300" style={{ fontSize: '0.875em', marginBottom: '0.5em' }}>
                    Your Email <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
                    style={{ padding: '0.5em 0.75em', fontSize: '0.875em' }}
                    placeholder="your@email.com"
                  />
                </div>

                {/* Bug Type */}
                <div>
                  <label htmlFor="bugType" className="block font-medium text-gray-300" style={{ fontSize: '0.875em', marginBottom: '0.5em' }}>
                    Bug Type <span className="text-red-400">*</span>
                  </label>
                  <select
                    id="bugType"
                    value={bugType}
                    onChange={(e) => setBugType(e.target.value)}
                    required
                    className="w-full bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
                    style={{ padding: '0.5em 0.75em', fontSize: '0.875em' }}
                  >
                    <option value="functionality">Functionality Issue</option>
                    <option value="display">Display/UI Issue</option>
                    <option value="performance">Performance Issue</option>
                    <option value="data">Data/Content Issue</option>
                    <option value="authentication">Login/Authentication Issue</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block font-medium text-gray-300" style={{ fontSize: '0.875em', marginBottom: '0.5em' }}>
                    Bug Description <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                    rows={3}
                    className="w-full bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 resize-none"
                    style={{ padding: '0.5em 0.75em', fontSize: '0.875em' }}
                    placeholder="Please describe the bug you encountered..."
                  />
                </div>

                {/* Steps to Reproduce */}
                <div>
                  <label htmlFor="steps" className="block font-medium text-gray-300" style={{ fontSize: '0.875em', marginBottom: '0.5em' }}>
                    Steps to Reproduce
                  </label>
                  <textarea
                    id="steps"
                    value={steps}
                    onChange={(e) => setSteps(e.target.value)}
                    rows={2}
                    className="w-full bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 resize-none"
                    style={{ padding: '0.5em 0.75em', fontSize: '0.875em' }}
                    placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
                  />
                </div>

                {/* Expected Behavior */}
                <div>
                  <label htmlFor="expectedBehavior" className="block font-medium text-gray-300" style={{ fontSize: '0.875em', marginBottom: '0.5em' }}>
                    Expected Behavior
                  </label>
                  <textarea
                    id="expectedBehavior"
                    value={expectedBehavior}
                    onChange={(e) => setExpectedBehavior(e.target.value)}
                    rows={2}
                    className="w-full bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 resize-none"
                    style={{ padding: '0.5em 0.75em', fontSize: '0.875em' }}
                    placeholder="What should have happened instead?"
                  />
                </div>

                {/* Browser */}
                <div>
                  <label htmlFor="browser" className="block font-medium text-gray-300" style={{ fontSize: '0.875em', marginBottom: '0.5em' }}>
                    Browser/Device
                  </label>
                  <input
                    type="text"
                    id="browser"
                    value={browser}
                    onChange={(e) => setBrowser(e.target.value)}
                    className="w-full bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
                    style={{ padding: '0.5em 0.75em', fontSize: '0.875em' }}
                    placeholder="e.g., Chrome on Windows, Safari on iPhone"
                  />
                </div>
              </div>
            )}
          </form>

          {/* Footer with buttons */}
          {!submitSuccess && (
            <div className="border-t border-gray-700 bg-gray-800/90 backdrop-blur-sm flex justify-end" style={{ padding: '1em', gap: '0.75em' }}>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors font-medium"
                style={{ padding: '0.5em 1em', fontSize: '0.875em' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={isSubmitting || !email || !description}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center"
                style={{ padding: '0.5em 1.25em', fontSize: '0.875em', gap: '0.5em' }}
              >
                {isSubmitting ? (
                  <>
                    <div className="border-2 border-white/30 border-t-white rounded-full animate-spin" style={{ width: '1em', height: '1em' }} />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send style={{ width: '1em', height: '1em' }} />
                    Send Report
                  </>
                )}
              </button>
            </div>
          )}
      </div>
    </div>
  )
}